import { createContext, useContext, useMemo, type ReactNode } from 'react';
import type { WeaponsResponse } from '../api/types';
import { useI18n } from '../i18n';

interface WeaponsContextValue {
  data: WeaponsResponse | null;
  /** "weapon_knife_karambit" → "Karambit" */
  weaponName: (code: string | null | undefined) => string;
  /** Categoria a que a arma pertence. */
  categoryOf: (code: string | null | undefined) => string | null;
  /** O código de filtro desta arma já foi validado contra o Youpin? */
  isConfirmed: (code: string | null | undefined) => boolean;
  /** "sticker" → "Adesivos" / "Stickers", conforme o idioma. */
  kindName: (key: string | null | undefined) => string;
  /** URL da arte usada como ícone da categoria. */
  kindIcon: (key: string | null | undefined) => string | null;
}

const WeaponsContext = createContext<WeaponsContextValue>({
  data: null,
  weaponName: (code) => code ?? '',
  categoryOf: () => null,
  isConfirmed: () => true,
  kindName: (key) => key ?? '',
  kindIcon: () => null,
});

export function WeaponsProvider({ data, children }: { data: WeaponsResponse | null; children: ReactNode }) {
  const { lang } = useI18n();
  const value = useMemo<WeaponsContextValue>(() => {
    const names = new Map<string, string>();
    const categories = new Map<string, string>();
    const confirmed = new Map<string, boolean>();
    for (const group of data?.groups ?? []) {
      for (const item of group.items) {
        names.set(item.code, item.name);
        categories.set(item.code, group.category);
        confirmed.set(item.code, item.confirmed);
      }
    }
    return {
      data,
      // Sem a taxonomia carregada ainda, o código cru é melhor que nada.
      weaponName: (code) => (code ? names.get(code) ?? code : ''),
      categoryOf: (code) => (code ? categories.get(code) ?? null : null),
      // Sem a taxonomia carregada, assume validado para não alarmar à toa.
      isConfirmed: (code) => (code ? confirmed.get(code) ?? true : true),
      kindIcon: (key) => (key ? data?.kindIcons?.[key] ?? null : null),
      kindName: (key) => {
        if (!key) return '';
        const kind = (data?.kinds ?? []).find((k) => k.key === key);
        if (!kind) return key;
        return lang === 'pt' ? kind.pt : kind.en;
      },
    };
  }, [data, lang]);

  return <WeaponsContext.Provider value={value}>{children}</WeaponsContext.Provider>;
}

export function useWeapons(): WeaponsContextValue {
  return useContext(WeaponsContext);
}
